/**
 * Palette Scope Component
 * Scopes color palette CSS variables to canvas/content area only
 * Prevents palette colors from affecting app UI (navbar, sidebar, etc.)
 */

import React, { useLayoutEffect, useRef } from 'react';
import { usePaletteAsCSS } from '@/hooks/theme/usePalette';
import type { GetActivePaletteParams } from '@/types';

interface PaletteScopeProps {
  children: React.ReactNode;
  paletteParams?: GetActivePaletteParams;
  className?: string;
}

export const PaletteScope: React.FC<PaletteScopeProps> = ({
  children,
  paletteParams,
  className,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const paletteCSSVars = usePaletteAsCSS(paletteParams);

  // Inject palette CSS variables on this container element only
  useLayoutEffect(() => {
    if (!containerRef.current || !paletteCSSVars) {
      return;
    }

    const element = containerRef.current;

    // Apply palette colors as CSS custom properties scoped to this element
    Object.entries(paletteCSSVars).forEach(([property, value]) => {
      element.style.setProperty(property, value);
    });

    // Cleanup: remove properties when component unmounts or palette changes
    return () => {
      Object.keys(paletteCSSVars).forEach((property) => {
        element.style.removeProperty(property);
      });
    };
  }, [paletteCSSVars]);

  return (
    <div ref={containerRef} className={className} data-palette-scope="true">
      {children}
    </div>
  );
};
